import React, { useEffect, useState } from 'react';
import { Alert, Button, CustomInput, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import toast from 'react-hot-toast';
import api from '../helpers/apis/apis';
import { ShieldOff } from 'react-feather';
import './RestrictedRegistry.css';

const formatDdMmYyyy = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
};

export default function RestrictedRegistry({ user, sourceType, sourceId, canMark = false, onChanged, compact = false, iconOnly = false }) {
    const [entry, setEntry] = useState(user && user.restricted_registry);
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setEntry(user && user.restricted_registry);
    }, [user]);

    if (!user || !user.id) return null;
    if (iconOnly && (entry || !canMark || !sourceId)) return null;

    const save = async (event) => {
        event.preventDefault();
        if (saving) return;
        if (!reason.trim()) {
            setError('Enter a reason for adding this user to Restricted Registry.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const response = await api.post('v1/admin/restricted-registry', {
                source_type: sourceType,
                source_id: sourceId,
                reason: reason.trim(),
            });
            setEntry(response.data);
            setOpen(false);
            setReason('');
            toast.success(response.message);
            if (onChanged) onChanged();
        } catch (err) {
            setError(err.response?.data?.errors?.reason?.[0] || err.response?.data?.message || 'Unable to save Restricted Registry entry.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={iconOnly ? 'd-inline-flex align-items-center' : compact ? 'my-1' : 'my-3'} style={{ textTransform: 'none', whiteSpace: 'normal' }}>
            {entry ? (
                <div className="border border-danger rounded p-2 text-danger" style={{ backgroundColor: '#fff2f2', minWidth: 180 }}>
                    <strong>Restricted Registry User</strong>
                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.reason}</div>
                    <small>Added by {entry.admin_name} {entry.created_at ? `on ${formatDdMmYyyy(entry.created_at)}` : ''}</small>
                    {!compact && <div className="mt-1">Internal only. This does not prevent approval or processing.</div>}
                </div>
            ) : canMark && sourceId ? (
                <Button size="sm" color={iconOnly ? 'link' : 'danger'} outline={!iconOnly}
                    className={iconOnly ? 'booking-action-button booking-action-button--registry' : undefined}
                    title="Add to Restricted Registry" aria-label="Add to Restricted Registry"
                    onClick={() => { setError(''); setOpen(true); }}>
                    {iconOnly ? <ShieldOff size={18} strokeWidth={1.8} aria-hidden="true" /> : 'Add to Restricted Registry'}
                </Button>
            ) : null}
            <Modal isOpen={open} toggle={() => !saving && setOpen(false)}>
                <Form onSubmit={save}>
                    <ModalHeader toggle={() => !saving && setOpen(false)}>Add to Restricted Registry</ModalHeader>
                    <ModalBody>
                        <p>Add <strong>{user.name}</strong> to the internal Restricted Registry. This will be visible to admins across their bookings and applications. The user will not be notified.</p>
                        <FormGroup>
                            <Label for={`registry-reason-${sourceType}-${sourceId}`}>Reason (required)</Label>
                            <Input id={`registry-reason-${sourceType}-${sourceId}`} type="textarea" rows="4"
                                required maxLength={2000} value={reason} disabled={saving}
                                onChange={(event) => setReason(event.target.value)} />
                        </FormGroup>
                        {error && <Alert color="danger">{error}</Alert>}
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" color="secondary" disabled={saving} onClick={() => setOpen(false)}>Cancel</Button>
                        <Button type="submit" color="danger" disabled={saving || !reason.trim()}>{saving ? 'Saving…' : 'Add to Restricted Registry'}</Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </div>
    );
}

export function RestrictedRegistryFilter({ value, onChange }) {
    return (
        <CustomInput type="switch" id="restricted-registry-filter" label="Restricted Registry"
            className="ml-3 my-1" checked={value === 'restricted'}
            onChange={(event) => onChange(event.target.checked ? 'restricted' : '')} />
    );
}

export function RestrictedRegistryName({ user }) {
    const [open, setOpen] = useState(false);
    const entry = user && user.restricted_registry;
    const name = (user && user.name) || '--';
    const capitalize = (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

    if (!entry) return <span>{capitalize(name)}</span>;

    return (
        <>
            <button type="button" className="btn btn-link text-danger font-weight-bold p-0 text-left"
                style={{ textDecoration: 'underline', whiteSpace: 'normal' }}
                title="Restricted Registry user — click for details"
                aria-label={`${name}: Restricted Registry user. View reason and administrator.`}
                onClick={() => setOpen(true)}>
                {capitalize(name)}
            </button>
            <Modal isOpen={open} toggle={() => setOpen(false)}>
                <ModalHeader toggle={() => setOpen(false)}>Restricted Registry User</ModalHeader>
                <ModalBody style={{ textTransform: 'none' }}>
                    <p className="text-danger font-weight-bold">{capitalize(name)}</p>
                    <dl className="mb-0">
                        <dt>Reason</dt>
                        <dd style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.reason}</dd>
                        <dt>Added by</dt>
                        <dd>{entry.admin_name}</dd>
                        {entry.created_at && <><dt>Added on</dt><dd>{formatDdMmYyyy(entry.created_at)}</dd></>}
                    </dl>
                </ModalBody>
                <ModalFooter><Button color="secondary" onClick={() => setOpen(false)}>Close</Button></ModalFooter>
            </Modal>
        </>
    );
}
